// useCanva
import { useContext, useEffect, useMemo, useState } from 'react';
import { showToast } from '../helpers/showToast';
import { GENERIC_ERROR_MESSAGE } from '../constants/apollo-errors';
import { loadExternalScriptToDOM } from '../helpers/loadExternalScriptToDOM';
import { AppContext } from '../providers/AppProvider';

// Extend the Window object to support Canva.
declare global {
  interface Window {
    Canva?: CanvaType;
  }
}

type OnDesignPublish = ({ exportUrl, designId }: { exportUrl: string; designId: string }) => void;

type CanvaOptions = {
  design: { id: string; dimensions: { width: number; height: number } };
  editor: { fileType: 'jpg'; publishLabel: string };
  onDesignPublish: OnDesignPublish;
};

type CanvaType = {
  createDesign: (options: CanvaOptions) => void;
  DesignButton?: {
    initialize: ({ apiKey }: { apiKey: string }) => Promise<CanvaType>;
  };
};

// Static configuration for Canva API Key
const CANVA_SCRIPT_SRC = 'https://sdk.canva.com/designbutton/v2/api.js';

// Wraps Canva calls to ensure they're safe even if Canva wasn't loaded.
const createSafeCanva = (isCanvaScriptLoaded: boolean): CanvaType => {
  return {
    createDesign: (options: CanvaOptions) => {
      if (isCanvaScriptLoaded && window.Canva) {
        window.Canva.createDesign(options);
      } else {
        console.error('Canva script is not loaded yet.');
      }
    }
  };
};

export const useCanva = () => {
  const { appConfig } = useContext(AppContext);
  const [isCanvaScriptLoaded, setIsCanvaScriptLoaded] = useState(false);
  const safeCanva = useMemo(() => createSafeCanva(isCanvaScriptLoaded), [isCanvaScriptLoaded]);
  const canvaApiKey = appConfig.canvaApiKey;

  useEffect(() => {
    if (!isCanvaScriptLoaded && canvaApiKey) {
      loadExternalScriptToDOM(CANVA_SCRIPT_SRC, 'canvaScript').then(() => {
        setIsCanvaScriptLoaded(true);
        if (window.Canva?.DesignButton) {
          window.Canva.DesignButton.initialize({ apiKey: canvaApiKey })
            .then(canva => {
              window.Canva = canva;
            })
            .catch(error => {
              // Giver user feedback
              showToast({
                severity: 'error',
                detail: GENERIC_ERROR_MESSAGE,
                summary: 'Unable to load Canva'
              });

              // Throw to be caught by reporting mechanics
              throw error;
            });
        }
      });
    }
  }, [isCanvaScriptLoaded, canvaApiKey]);

  return {
    canva: safeCanva
  };
};
